import Languages from './Languages.js'
import {trackTraceLink} from './trackTraceLink.js'
import {TRACK_TRACE_ACTION_PARAMS, TrackTraceActions, TrackTraceView} from '../types/trackTrace.js'

// Content of the mails to recipients of an order in the style of the Track & Trace page (#3845): the status card,
// the options to change the delivery and the footer. Shared by the server (mail.ts) and the preview in the settings,
// so it gets everything it needs as plain data. The texts are Dutch, like all order mails.

type MailAddress = {
    type?: 'pickup' | 'delivery'
    street?: string
    nr?: string
    addition?: string
    postalCode?: string
    city?: string
    startTime?: string
    endTime?: string
    estimatedTimeOfArrival?: string
    timeOfArrival?: string
    verificationCode?: string
    signatureRequired?: boolean
    statedAddressOnly?: boolean
    mailboxDelivery?: boolean
    minimumAge?: number | boolean
    idCheck?: boolean
}

export interface TrackTraceMailInput {
    notification: string
    action: string
    today: string // YYYY-MM-DD, for Vandaag and Morgen
    trackTrace: string
    addressIndex: number
    address: MailAddress
    date: string | null // YYYY-MM-DD of the order
    // A failed delivery that is not rescheduled (missing collo, address problem): the new date is unknown
    undeliverable?: boolean
    timeFrame: number // minutes around the ETA
    // How a delivered shipment was left, from the Track & Trace view; without it the action of the notification
    outcome: TrackTraceView['outcome']
    neighbourNr?: string
    // The pickup point the recipient chose, for a shipment on hold
    pickupPoint: {name?: string, address?: string, openingHours?: string, until?: string} | null
    colliCount: number
    senderName: string // The customer that sends the shipment, {klant} in the messages
    customerName: string // The customer account, to contact after a failed pickup
    actions: TrackTraceActions | null
    domain: string
    branding: {resellerName: string, phone: string, email: string, companyAddress: {street: string, nr: string, addition: string, postalCode: string, city: string} | null}
}

export type TrackTraceMailIcon = 'map-marker-outline' | 'package-variant-closed' | 'store-marker-outline' | 'check-circle-outline'

export interface TrackTraceMailContent {
    pickup: boolean
    // Segments of the progress bar that are done (of 4); null after a failed attempt
    progress: number | null
    status: string
    warn: boolean
    moment: {label: string, day: string, time: string, timeMuted: boolean} | null
    rows: {icon: TrackTraceMailIcon, label: string, value: string}[]
    notices: string[]
    // The notices ask the recipient to act (contact the customer after a failed pickup)
    noticesWarn: boolean
    // The delivery code with the requirement as its label
    code: {label: string, value: string} | null
    button: {label: string, href: string}
    actions: {key: string, icon: string, label: string, href: string}[]
    footer: string[]
}

const NL = 'NL'

function shiftTime(time: string, minutes: number): string {
    const [hours, mins] = time.split(':').map(Number)
    const total = ((hours * 60 + mins + minutes) % 1440 + 1440) % 1440
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/** Time window of an address as on the Track & Trace page: 1/3 of the time frame before the ETA and 2/3 after. */
export function etaWindow(address: Pick<MailAddress, 'estimatedTimeOfArrival' | 'startTime' | 'endTime'>, timeFrame: number): {from: string, to: string} | null {
    if (address.estimatedTimeOfArrival) {
        return {from: shiftTime(address.estimatedTimeOfArrival, -Math.floor(timeFrame / 3)), to: shiftTime(address.estimatedTimeOfArrival, Math.floor(timeFrame * 2 / 3))}
    }

    return address.startTime && address.endTime ? {from: address.startTime, to: address.endTime} : null
}

function addDays(date: string, days: number): string {
    const [year, month, day] = date.split('-').map(Number)
    return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10)
}

/** Vandaag, Morgen or the weekday with the date, e.g. Dinsdag 10 maart. */
export function mailDay(date: string, today: string): string {
    if (date === today) {
        return Languages.mailToday[NL]
    }

    if (date === addDays(today, 1)) {
        return Languages.mailTomorrow[NL]
    }

    const [year, month, day] = date.split('-').map(Number)
    const text = new Intl.DateTimeFormat('nl-NL', {weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC'}).format(new Date(Date.UTC(year, month - 1, day)))

    return text.charAt(0).toUpperCase() + text.slice(1)
}

const DELIVERY_STEPS = () => [Languages.mailStepRegistered[NL], Languages.mailStepScheduled[NL], Languages.mailStepOnTheWay[NL], Languages.mailStepDelivered[NL]]
const PICKUP_STEPS = () => [Languages.mailStepRegistered[NL], Languages.mailStepScheduled[NL], Languages.mailStepOnTheWay[NL], Languages.mailStepCollected[NL]]

// Step (1-4) of each notification, as the step of the page when it is sent; a received parcel is still step 1
const STEPS: Record<string, number> = {
    pickup_registered: 1,
    pickup_eta: 2,
    pickup_ontheway: 3,
    pickup_completed: 4,
    delivery_registered: 1,
    delivery_collected: 1,
    delivery_eta: 2,
    delivery_ontheway: 3,
    delivery_completed: 4
}

const TILES: {key: keyof typeof TRACK_TRACE_ACTION_PARAMS, icon: string, label: () => string}[] = [
    {key: 'safePlace', icon: 'tile-home-outline', label: () => Languages.mailTileSafePlace[NL]},
    {key: 'neighbour', icon: 'tile-account-multiple-outline', label: () => Languages.mailTileNeighbour[NL]},
    {key: 'pickupPoint', icon: 'tile-store-marker-outline', label: () => Languages.mailTilePickupPoint[NL]},
    {key: 'instructions', icon: 'tile-message-text-outline', label: () => Languages.mailTileInstructions[NL]},
    {key: 'deliveryDay', icon: 'tile-calendar-refresh', label: () => Languages.mailTileDeliveryDay[NL]}
]

/** Whether the mail offers the options to change the delivery: delivery mails before the delivery. */
export function mailOffersActions(notification: string, action: string): boolean {
    return ['delivery_registered', 'delivery_collected', 'delivery_eta', 'delivery_ontheway'].includes(notification) ||
        (['delivery_delayed', 'delivery_nothome'].includes(notification) && action === 'reschedule')
}

function requirementLines(address: MailAddress): string[] {
    if (address.type !== 'delivery') {
        return []
    }

    return [
        address.signatureRequired && Languages.mailReqSignature[NL],
        address.statedAddressOnly && !address.mailboxDelivery && Languages.mailReqStatedAddressOnly[NL],
        address.mailboxDelivery && Languages.mailReqMailbox[NL],
        typeof address.minimumAge === 'number' && address.minimumAge && Languages.mailReqMinimumAge[NL](address.minimumAge),
        address.idCheck && Languages.mailReqIdCheck[NL]
    ].filter(Boolean) as string[]
}

function deliveredText(input: TrackTraceMailInput): string {
    const outcome = input.outcome

    if (outcome?.neighbourNr || input.action === 'deliveredAtNeighbors') {
        return Languages.mailDeliveredNeighbour[NL](outcome?.neighbourNr || input.neighbourNr || '')
    }

    if (outcome?.mailbox || input.action === 'deliveredInMailbox') {
        return Languages.mailDeliveredMailbox[NL]
    }

    if (outcome?.safePlace) {
        return Languages.mailDeliveredSafePlace[NL](outcome.safePlace)
    }

    return ''
}

export function trackTraceMailContent(input: TrackTraceMailInput): TrackTraceMailContent {
    const {notification, action, address} = input
    const pickup = address.type === 'pickup'
    const steps = pickup ? PICKUP_STEPS() : DELIVERY_STEPS()
    const step = STEPS[notification] || null

    const link = (param?: string) => trackTraceLink(input.trackTrace, address, input.addressIndex, input.domain, param)
    const day = (date: string | null) => date ? mailDay(date, input.today) : Languages.mailDateUnknown[NL]
    const window = etaWindow(address, input.timeFrame)
    const windowFollows = pickup ? Languages.mailWindowFollowsPickup[NL] : Languages.mailWindowFollows[NL]
    const failure = notification.endsWith('_delayed') ? Languages.mailNotCompleted[NL] : Languages.mailNotHome[NL]

    let status = step ? Languages.mailStep[NL]({step, status: steps[step - 1]}) : ''
    let moment: TrackTraceMailContent['moment'] = null
    const notices: string[] = []

    if (['pickup_registered', 'delivery_registered', 'delivery_collected'].includes(notification)) {
        moment = {label: pickup ? Languages.mailExpectedPickup[NL] : Languages.mailExpectedDelivery[NL], day: day(input.date), time: windowFollows, timeMuted: true}
    } else if (['pickup_eta', 'pickup_ontheway', 'delivery_eta', 'delivery_ontheway'].includes(notification)) {
        moment = {
            label: pickup ? Languages.mailExpectedPickup[NL] : Languages.mailExpectedDelivery[NL],
            day: day(input.date),
            time: window ? Languages.mailWindow[NL](window) : windowFollows,
            timeMuted: !window
        }
    } else if (notification === 'pickup_completed' || notification === 'delivery_completed') {
        moment = {
            label: pickup ? Languages.mailCollectedOn[NL] : Languages.mailDeliveredOn[NL],
            day: day(input.date),
            time: address.timeOfArrival ? Languages.mailArrivedAt[NL](address.timeOfArrival) : '',
            timeMuted: false
        }
    } else if (notification === 'pickup_delayed' || notification === 'pickup_nothome') {
        status = failure
        notices.push(Languages.mailPickupContact[NL](input.customerName))
    } else if (notification === 'delivery_delayed' || notification === 'delivery_nothome') {
        if (action === 'onhold') {
            status = Languages.mailOnHold[NL]
            moment = {label: Languages.mailPickupUntil[NL], day: day(input.pickupPoint?.until || input.date), time: '', timeMuted: false}
        } else if (action === 'reschedule') {
            status = Languages.mailFollowUp[NL]({status: failure, followUp: Languages.mailFollowUpReschedule[NL]})
            moment = {label: Languages.mailNewDelivery[NL], day: input.undeliverable ? Languages.mailDateUnknown[NL] : day(input.date), time: input.undeliverable ? '' : windowFollows, timeMuted: true}
        } else {
            status = Languages.mailFollowUp[NL]({status: failure, followUp: action === 'retour' ? Languages.mailFollowUpRetour[NL] : Languages.mailFollowUpNone[NL]})
        }
    }

    const rows: TrackTraceMailContent['rows'] = [{
        icon: 'map-marker-outline',
        label: pickup ? Languages.collectionAddress[NL] : Languages.deliveryAddress[NL],
        value: [[address.street, `${address.nr || ''}${address.addition || ''}`].filter(Boolean).join(' '), [address.postalCode, address.city].filter(Boolean).join(' ')].filter(Boolean).join('\n')
    }]

    if (input.colliCount > 0) {
        rows.push({icon: 'package-variant-closed', label: Languages.mailShipment[NL], value: Languages.mailShipmentOf[NL]({count: input.colliCount, sender: input.senderName})})
    }

    if (action === 'onhold' && input.pickupPoint) {
        rows.push({icon: 'store-marker-outline', label: Languages.pickupPoint[NL], value: [input.pickupPoint.name, input.pickupPoint.address].filter(Boolean).join('\n')})

        if (input.pickupPoint.openingHours) {
            rows.push({icon: 'store-marker-outline', label: Languages.mailOpeningHours[NL], value: input.pickupPoint.openingHours})
        }
    }

    const delivered = notification === 'delivery_completed' ? deliveredText(input) : ''

    if (delivered) {
        rows.push({icon: 'check-circle-outline', label: Languages.mailDelivered[NL], value: delivered})
    }

    // Requirements only matter while the shipment still has to be delivered
    const open = !['delivery_completed', 'pickup_completed'].includes(notification) && !['retour', 'nofollowup'].includes(action)
    const requirements = open ? requirementLines(address) : []

    const tiles = mailOffersActions(notification, action) && input.actions ?
        TILES.filter(({key}) => input.actions[key]?.available).map(({key, icon, label}) => ({key, icon, label: label(), href: link(TRACK_TRACE_ACTION_PARAMS[key])})) :
        []

    const buttonLabel = notification.endsWith('_ontheway') ?
        Languages.mailFollowCourier[NL] :
        notification === 'delivery_completed' ? Languages.mailViewProof[NL] : Languages.followShipment[NL]

    const companyAddress = input.branding.companyAddress

    return {
        pickup,
        progress: step,
        status,
        warn: !step,
        moment,
        rows,
        notices: [...notices, ...requirements],
        noticesWarn: notices.length > 0,
        code: open && address.type === 'delivery' && address.verificationCode ? {label: Languages.mailReqVerificationCode[NL], value: address.verificationCode} : null,
        button: {label: buttonLabel, href: link()},
        actions: tiles,
        footer: [
            input.branding.resellerName,
            input.branding.phone || input.branding.email ? Languages.mailFooterContact[NL]({phone: input.branding.phone, email: input.branding.email}) : '',
            companyAddress ? [[companyAddress.street, `${companyAddress.nr}${companyAddress.addition}`].filter(Boolean).join(' '), [companyAddress.postalCode, companyAddress.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') : '',
            input.senderName ? Languages.mailFooterReason[NL](input.senderName) : ''
        ].filter(Boolean)
    }
}
